import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export enum CustomerProductEvent {
  BACK_IN_STOCK = "BACK_IN_STOCK",
  LOW_INVENTORY = "LOW_INVENTORY",
}

export enum ProductNotificationStatus {
  WAITING = "waiting",
  NOTIFIED = "notified",
}

export enum ProductNotificationGroupBy {
  PRODUCT = "product",
  VARIANT = "variant",
}

export enum VariantStockStatus {
  IN_STOCK = "in_stock",
  OUT_OF_STOCK = "out_of_stock",
}

export const CustomerProductNotificationDbParser = z.object({
  _id: zExt.objectId(),
  customerEmail: z.string().optional(),
  customerPhone: z.string().optional(),
  teamId: zExt.objectId(),
  productId: z.string(),
  variantId: z.string(),
  subscribedEvent: createEnum(CustomerProductEvent),
  notificationSentAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomerProductNotification = z.infer<
  typeof CustomerProductNotificationDbParser
>;

export const CustomerProductNotificationDbParserArray = z.array(
  CustomerProductNotificationDbParser,
);

const backInStockProductBase = z.object({
  id: z.string(),
  productId: z.string(),
  name: z.string(),
  imageUrl: z.string().optional(),
  waitingCount: z.number(),
  notifiedCount: z.number(),
  potentialRevenue: z.number(),
  avgWaitDays: z.number(),
});

export const BackInStockVariantSchema = backInStockProductBase.extend({
  groupBy: z.literal(ProductNotificationGroupBy.VARIANT),
  variantId: z.string(),
  sku: z.string(),
  status: createEnum(VariantStockStatus),
  price: z.number(),
});

export const BackInStockVariantDetailSchema = z.object({
  variantId: z.string(),
  title: z.string(),
  sku: z.string(),
  status: createEnum(VariantStockStatus),
  waitingCount: z.number(),
  notifiedCount: z.number(),
  price: z.number(),
});

export type BackInStockVariantDetail = z.infer<
  typeof BackInStockVariantDetailSchema
>;

export const BackInStockProductGroupSchema = backInStockProductBase.extend({
  groupBy: z.literal(ProductNotificationGroupBy.PRODUCT),
  variantCount: z.number(),
  inStockCount: z.number(),
  outOfStockCount: z.number(),
  variantIds: z.array(z.string()),
  variants: z.array(BackInStockVariantDetailSchema),
});

export const BackInStockProductSchema = z.discriminatedUnion("groupBy", [
  BackInStockVariantSchema,
  BackInStockProductGroupSchema,
]);

export type BackInStockVariant = z.infer<typeof BackInStockVariantSchema>;
export type BackInStockProductGroup = z.infer<
  typeof BackInStockProductGroupSchema
>;
export type BackInStockProduct = z.infer<typeof BackInStockProductSchema>;

export const BackInStockSubscriberSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  variantId: z.string().optional(),
  variantTitle: z.string().optional(),
  subscribedDate: z.date(),
  status: createEnum(["waiting", "notified"]),
  notifiedDate: z.date().optional(),
  waitingDays: z.number().optional(),
});

export type BackInStockSubscriber = z.infer<typeof BackInStockSubscriberSchema>;
